import { ReactElement, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import queryString from 'query-string';
import { Box, Grid, Typography } from '@mui/material';
import { SignUpSchema } from 'validators/AuthenticationShema';
import { AuthenticationService } from 'services';
import {
    AppFormikTextField,
    AppFormikPasswordField,
    AppPrimaryButton,
} from 'shared/components/AppComponents';
import { Utils } from 'shared/helpers';

const SignupForm = (): ReactElement => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = queryString.parse(location.search);
    const { invitationId, orgId, emailId } = queryParams;
    const [signupForm, setSignupForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });

    // API Calls
    const signup = (formValues: any, callback: any): void => {
        AuthenticationService.register(formValues)
            .then((response: any) => {
                callback(true, response);
            })
            .catch((error: any) => {
                callback(false);
                Utils.handleErrorResponse(error);
            });
    };

    // UI Actions
    const actionOnFormSubmit = (formValues: any, formik: any): void => {
        const { firstName, lastName, email, password } = formValues;
        const requestObj = {
            firstName,
            lastName,
            email,
            password,
            invitationId,
            orgId,
        };
        signup(requestObj, (status: any, response: any) => {
            formik.setSubmitting(false);
            if (status) {
                Utils.handleSuccessResponse(response);
                const { isVerified } = response.data.data;
                if (isVerified) {
                    navigate('/login', {
                        replace: true,
                    });
                } else {
                    navigate('/email-confirmation', {
                        replace: true,
                        state: {
                            email,
                        },
                    });
                }
            }
        });
    };

    // Hooks
    useEffect(() => {
        if (emailId) {
            setSignupForm({ ...signupForm, email: `${emailId}` });
        }
    }, [location.pathname]);

    // UI Elements
    return (
        <Box className="signup-form-wrapper">
            <Typography className="title" variant="h2">
                Sign up
            </Typography>
            <Typography className="subtitle-line-1" variant="body1">
                Enter your details to create your account.
                <Typography className="subtitle-line-2" variant="body1">
                    Are you already registered?
                    <Typography
                        to="/login"
                        className="subtitle-link"
                        component={NavLink}
                        variant="body1"
                    >
                        Login
                    </Typography>
                </Typography>
            </Typography>

            <Box mt={7}>
                <Formik
                    enableReinitialize
                    initialValues={signupForm}
                    validationSchema={SignUpSchema}
                    validateOnMount
                    onSubmit={(formValues, formik) => {
                        actionOnFormSubmit(formValues, formik);
                    }}
                >
                    {({ handleSubmit, isSubmitting, isValid }) => (
                        <form autoComplete="off">
                            <Grid container spacing={3}>
                                <Grid item sm={6} xs={12}>
                                    <AppFormikTextField
                                        size="small"
                                        outerLabel
                                        label="First name *"
                                        name="firstName"
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <AppFormikTextField
                                        size="small"
                                        outerLabel
                                        label="Last name *"
                                        name="lastName"
                                    />
                                </Grid>
                                <Grid item sm={12} xs={12}>
                                    <AppFormikTextField
                                        size="small"
                                        outerLabel
                                        label="Email *"
                                        name="email"
                                        inputProps={{ readOnly: emailId }}
                                    />
                                </Grid>

                                <Grid item sm={6} xs={12}>
                                    <AppFormikPasswordField
                                        size="small"
                                        outerLabel
                                        label="Password *"
                                        name="password"
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <AppFormikPasswordField
                                        size="small"
                                        outerLabel
                                        label="Confirm password *"
                                        name="confirmPassword"
                                    />
                                </Grid>
                                <Grid item sm={12} xs={12}>
                                    <AppFormikTextField
                                        size="small"
                                        outerLabel
                                        label="Account name *"
                                        name="accountName"
                                        inputProps={{ readOnly: emailId }}
                                    />
                                </Grid>
                            </Grid>

                            <Box pt={6} className="justify-center">
                                <AppPrimaryButton
                                    className="btn-small"
                                    title="Sign up"
                                    isLoading={isSubmitting}
                                    loadingSize={22}
                                    loadingText="SIGNING UP"
                                    disabled={isSubmitting || !isValid}
                                    onClick={handleSubmit}
                                    fullWidth
                                />
                            </Box>
                        </form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};

export default SignupForm;
